import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, formatErr, tokenStore } from "../lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);

// Inactivity timeout: 30 minutes
const INACTIVITY_MS = 30 * 60 * 1000;
const LAST_ACTIVITY_KEY = "convic_last_activity";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [company, setCompany] = useState(null);
  const inactivityTimerRef = useRef(null);

  const loadMe = useCallback(async () => {
    const token = tokenStore.get();
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      // Inactivity guard applies ONLY to admin accounts.
      if (data?.role === "admin") {
        try {
          const last = parseInt(sessionStorage.getItem(LAST_ACTIVITY_KEY) || "0", 10);
          if (last && Date.now() - last > INACTIVITY_MS) {
            tokenStore.clear();
            sessionStorage.removeItem(LAST_ACTIVITY_KEY);
            setUser(false);
            return;
          }
        } catch {}
      }
      setUser(data);
      const c = await api.get("/company");
      setCompany(c.data);
    } catch {
      tokenStore.clear();
      setUser(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    tokenStore.set(data.access_token);
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    setUser(data.user);
    try {
      const c = await api.get("/company");
      setCompany(c.data);
    } catch {}
    return data.user;
  };

  const logout = useCallback(() => {
    tokenStore.clear();
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);
    setUser(false);
  }, []);

  // ---------- 30-min inactivity auto-logout (admin-only) ----------
  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") return; // Inactivity timer applies only to admin accounts
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    const resetTimer = () => {
      sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        toast.error("Sesiune expirată după 30 de minute de inactivitate. Te rog autentifică-te din nou.", { duration: 7000 });
        logout();
      }, INACTIVITY_MS);
    };
    resetTimer();
    events.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, resetTimer));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [user, logout]);

  // ---------- Auto-logout on 401 from any API call ----------
  useEffect(() => {
    const id = api.interceptors.response.use(
      (resp) => resp,
      (error) => {
        if (error?.response?.status === 401 && tokenStore.get()) {
          // Token rejected by backend — clear and force re-login.
          tokenStore.clear();
          sessionStorage.removeItem(LAST_ACTIVITY_KEY);
          setUser(false);
          toast.error("Sesiunea a expirat. Te rog autentifică-te din nou.");
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const refreshCompany = async () => {
    const c = await api.get("/company");
    setCompany(c.data);
  };

  const isAdmin = user && user.role === "admin";

  const canDo = (moduleCode, action) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (moduleCode === "COMBUSTIBIL" && action === "add") return true;
    if (action === "delete" && (moduleCode === "COMBUSTIBIL" || moduleCode === "PERSONAL")) return false;
    const p = (user.permissions || {})[moduleCode] || {};
    return !!p[action];
  };

  return (
    <AuthCtx.Provider value={{ user, company, login, logout, isAdmin, canDo, refreshCompany, setUser, formatErr }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
